/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @updatedOn Dec/01/2024
 */
export default function ArrayHelperService() {

  function IS_EMPTY<T>(array: readonly T[]): boolean {
    return !array || array.length <= 0;
  }

  function IS_NOT_EMPTY<T>(array: readonly T[]): boolean {
    return array && array.length > 0;
  }

  function SIZE_MATCH<L, R>(leftArray: readonly L[], rightArray: readonly R[]): boolean {
    if (IS_EMPTY(leftArray) && IS_EMPTY(rightArray)) {
      return true;
    } else if (IS_NOT_EMPTY(leftArray) && IS_NOT_EMPTY(rightArray)) {
      return leftArray.length === rightArray.length
    } else {
      return false;
    }
  }

  function SIZE_DONT_MATCH<L, R>(leftArray: readonly L[], rightArray: readonly R[]): boolean {
    return !SIZE_MATCH(leftArray, rightArray);
  }

  function NORMALIZE_KEYS(data: any[]): any[] {

    if (data?.length > 0) {
      const keys = Object.keys(data[0]);
      let newData = data.map((element: any) => {
        let newElement: any = {};

        keys.forEach(key => {
          const newKey = key.replace('[', '');
          newElement[newKey] = element[key];
        });

        return newElement;
      });
      return newData;
    } else {
      return data;
    }
  }

  function DENORMALIZE_KEYS(data: any[]): any[] {

    const keys = Object.keys(data[0]);
    let newData = data.map((element: any) => {
      let newElement: any = {};

      keys.forEach(key => {
        const newKey = key.replace('_____', '].[').replace('___', '[',).replace('___', ']');
        newElement[newKey] = element[key];
      });

      return newElement;
    });

    return newData;
  }

  function EXCEPT(data: any[], exceptIndex: number) {
    var cloneArray = data.slice();
    return cloneArray.splice(exceptIndex, 1);
  }

  /**
 * Compares two arrays of objects to determine if there are differences based on specified keys.
 * If no keys are provided, all keys in the objects are compared.
 *
 * @template T - The type of objects in the arrays.
 * @param keysToCompare - An optional array of keys to compare. If undefined, all keys are used.
 * @param array1 - The first array of objects to compare.
 * @param array2 - The second array of objects to compare.
 * @returns `true` if there are differences between the arrays; otherwise, `false`.
 *
 * @example
 * const array1 = [{ id: 1, name: 'Pradeep' }, { id: 2, name: 'John' }];
 * const array2 = [{ id: 1, name: 'Smith' }, { id: 2, name: 'Pradeep' }];
 * 
 * checkIsDifferentOnSameType(['id'], array1, array2); // false
 * checkIsDifferentOnSameType(undefined, array1, array2); // true
 *
 * @remarks
 * - The function handles null or undefined arrays gracefully.
 * - If arrays have different lengths, they are considered different.
 * - Uses a `Set` for efficient lookups, ensuring optimal performance.
 */
  function checkIsDifferentOnSameType<T>(
    array1?: T[] | null, array2?: T[] | null,
    keysToCompare: (keyof T)[] | undefined = undefined
  ): boolean {
    // Handle null or undefined arrays
    if (!array1 && !array2) return false; // Both are null/undefined
    if (!array1 || !array2) return true; // One is null/undefined, the other is not

    // Check if lengths are different
    if (array1.length !== array2.length) return true;

    // If keysToCompare is not provided, derive keys dynamically
    const allKeys = keysToCompare ?? (array1[0] ? (Object.keys(array1[0]) as (keyof T)[]) : []);

    // Create a map for quick lookup using combined key values as a unique identifier
    const createKey = (obj: T) => allKeys.map(key => obj[key]).join('|');
    const map1 = new Set(array1.map(createKey));

    // Check if any object in array2 has a different combined key
    return array2.some(obj => !map1.has(createKey(obj)));
  }


  function checkIsDifferentOnCrossType<T, K>(
    keyToCompare: keyof T & keyof K,
    array1: T[], array2: K[]
  ) {
    // Handle null or undefined arrays
    if (!array1 && !array2) return false; // Both are null/undefined
    if (!array1 || !array2) return true; // One is null/undefined, the other is not

    // Check if lengths are different
    if (array1.length !== array2.length) return true;

    // Create a map from the first array
    const valuesOfArray1 = array1.map(x => x[keyToCompare]);
    const valuesOfArray2 = array1.map(x => x[keyToCompare]);

    // Check for differences in values
    return (
      valuesOfArray1.some(value => !valuesOfArray2.includes(value)) ||
      valuesOfArray2.some(value => !valuesOfArray1.includes(value))
    );
  }

  /**
 * @author Pradeep <pradeep.rajendran@rldatix.com>
 * @createdOn Dec/13/2024
 * Sorts an array of objects by a specified key. First, it sorts alphabetically, 
 * and then by the length of the string representation of the key's value.
 * 
 * @template T - Generic type representing the structure of the objects in the array.
 * @param keyToSort - The key in the objects whose values will be used for sorting.
 * @param data - The array of objects to be sorted.
 * @returns A new array sorted alphabetically and then by string length.
 * 
 * @remarks
 * - The function supports mixed types by converting all key values to strings.
 * - Primary sort is alphabetical, ensuring that the resulting array is lexicographically ordered.
 * - Secondary sort is by length, grouping shorter strings before longer ones.
 * - The function leverages JavaScript's stable sort behavior, preserving alphabetical order for strings of the same length.
 */
  function sortAlphabeticallyThenByLength<T>(keyToSort: keyof T, data: T[]): T[] {
    return data.sort((a, b) => {
      const valA = a[keyToSort] + '';
      const valB = b[keyToSort] + '';

      // Primary: Group by the first character alphabetically
      const firstCharDiff = valA[0].localeCompare(valB[0]);
      if (firstCharDiff !== 0) {
        return firstCharDiff;
      }

      // Secondary: Sort by length within the same alphabetical group
      return valA.length - valB.length;
    });
  }


  // ---
  return {
    IS_EMPTY, IS_NOT_EMPTY, SIZE_MATCH, SIZE_DONT_MATCH,
    EXCEPT, checkIsDifferentOnSameType, checkIsDifferentOnCrossType,
    NORMALIZE_KEYS, DENORMALIZE_KEYS,
    sortAlphabeticallyThenByLength,
  }
}